import { type FC } from "react";
import { Alert, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { StreamSignalLike } from "@/partner/entities/streams";
import {
  MonitoringGranularityType,
  MonitoringUnavailableReasonType,
} from "@/types/graphql/partner.generated";
import type { MonitoringPeriod } from "../model/period";
import type {
  ChartTimeUnit,
  LiveInputMetricsPoint,
  LiveInputMonitoring,
} from "../model/types";
import { MonitoringEventLog } from "./MonitoringEventLog";
import {
  MonitoringLineChart,
  type MonitoringSeries,
} from "./MonitoringLineChart";
import { MonitoringSummaryCard } from "./MonitoringSummaryCard";
import { PlayerMetricsSection } from "./PlayerMetricsSection";

export interface LiveInputMonitoringViewProps {
  monitoring: LiveInputMonitoring | null | undefined;
  loading: boolean;
  error?: unknown;
  period: MonitoringPeriod;
  periods: MonitoringPeriod[];
  onChangePeriod: (period: MonitoringPeriod) => void;
  viewersOnline?: number | null;
  /** Сигнал «есть ли видео»; null, если эфир не идёт или ещё не проверен. */
  signal?: StreamSignalLike | null;
}

// Ключ i18n только статический литерал: причина недоступности через мапу.
const UNAVAILABLE_REASON_KEYS: Record<MonitoringUnavailableReasonType, string> =
  {
    [MonitoringUnavailableReasonType.NotAuthorizedType]:
      "streams.monitoring.unavailable.NOT_AUTHORIZED",
    [MonitoringUnavailableReasonType.CloudflareErrorType]:
      "streams.monitoring.unavailable.CLOUDFLARE_ERROR",
    [MonitoringUnavailableReasonType.NotSupportedType]:
      "streams.monitoring.unavailable.NOT_SUPPORTED",
  };

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return String(error);
}

export const LiveInputMonitoringView: FC<LiveInputMonitoringViewProps> = ({
  monitoring,
  loading,
  error,
  period,
  periods,
  onChangePeriod,
  viewersOnline,
  signal = null,
}) => {
  const { t } = useTranslation();

  const unit: ChartTimeUnit =
    monitoring?.granularity === MonitoringGranularityType.MinuteType
      ? "minute"
      : "hour";
  const points = monitoring?.points ?? [];
  const series = (
    pick: (point: LiveInputMetricsPoint) => number | null | undefined
  ): MonitoringSeries[] => [
    {
      label: "",
      data: points.map((point) => ({ x: point.at, y: pick(point) ?? null })),
    },
  ];
  const noData = points.length === 0;
  const bands = monitoring?.outages.periods ?? [];
  const unavailableReason =
    monitoring?.unavailableReason ??
    MonitoringUnavailableReasonType.CloudflareErrorType;

  return (
    <Stack spacing={2}>
      <MonitoringSummaryCard
        signal={signal}
        status={monitoring?.status ?? null}
        outages={monitoring?.available ? monitoring.outages : null}
        viewersOnline={viewersOnline}
        period={period}
        periods={periods}
        onChangePeriod={onChangePeriod}
      />
      {error ? <Alert severity="error">{errorMessage(error)}</Alert> : null}
      {monitoring && !monitoring.available ? (
        <Alert
          severity={
            unavailableReason ===
            MonitoringUnavailableReasonType.NotAuthorizedType
              ? "warning"
              : "info"
          }
        >
          {t(UNAVAILABLE_REASON_KEYS[unavailableReason])}
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <MonitoringLineChart
            title={t("streams.monitoring.charts.bitrate")}
            hint={t("streams.monitoring.charts.bitrateInfo")}
            bands={bands}
            yLabel={t("streams.monitoring.units.kbps")}
            unit={unit}
            series={series((point) => point.bitRateKbps)}
            loading={loading}
            empty={noData}
            emptyLabel={t("streams.monitoring.noData")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MonitoringLineChart
            title={t("streams.monitoring.charts.keyframe")}
            hint={t("streams.monitoring.charts.keyframeInfo")}
            bands={bands}
            yLabel={t("streams.monitoring.units.seconds")}
            unit={unit}
            series={series((point) => point.keyframeIntervalSeconds)}
            loading={loading}
            empty={noData}
            emptyLabel={t("streams.monitoring.noData")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MonitoringLineChart
            title={t("streams.monitoring.charts.uploadSpeed")}
            hint={t("streams.monitoring.charts.uploadSpeedInfo")}
            bands={bands}
            yLabel="%"
            unit={unit}
            referenceY={100}
            series={series((point) => point.uploadSpeedPercent)}
            loading={loading}
            empty={noData}
            emptyLabel={t("streams.monitoring.noData")}
          />
        </Grid>
      </Grid>
      <PlayerMetricsSection
        player={monitoring?.player ?? null}
        unit={unit}
        loading={loading}
      />
      {/* Журнал в самом низу и свёрнут: длинный список не уводит метрики
          плеера за край вкладки (Борис 24.10). */}
      <MonitoringEventLog
        events={monitoring?.events ?? []}
        sessions={monitoring?.sessions ?? []}
        outages={monitoring?.available ? monitoring.outages : null}
        loading={loading}
      />
    </Stack>
  );
};
